/**
 * Framework-agnostic connectivity store.
 *
 * A single source of truth for whether the app can reach the server. The axios
 * client reports success/failure of real API calls (see `services/api/client.ts`)
 * and the browser's `online`/`offline` events report device connectivity. The
 * React layer subscribes via `useConnectivity` (useSyncExternalStore).
 *
 * Three derived states:
 * - `online`              — device online and the last API call succeeded.
 * - `offline`             — `navigator.onLine` is false.
 * - `server-unavailable`  — device is online but the last API call failed to
 *                           reach the server (ERR_NETWORK / timeout / 5xx).
 *
 * On reconnect we optimistically assume the server is reachable again; the next
 * failing call re-trips the banner, the next success clears it.
 */

export type ConnectivityStatus = 'online' | 'offline' | 'server-unavailable';

export interface ConnectivityState {
  online: boolean;
  serverReachable: boolean;
  status: ConnectivityStatus;
}

function computeStatus(
  online: boolean,
  serverReachable: boolean,
): ConnectivityStatus {
  if (!online) return 'offline';
  if (!serverReachable) return 'server-unavailable';
  return 'online';
}

const initialState: ConnectivityState = {
  // navigator.onLine is true even before any network activity; assume reachable
  // until a real API call proves otherwise.
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  serverReachable: true,
  status:
    typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'offline'
      : 'online',
};

let state: ConnectivityState = initialState;
const listeners = new Set<() => void>();

function setState(next: { online: boolean; serverReachable: boolean }): void {
  const status = computeStatus(next.online, next.serverReachable);
  if (
    next.online === state.online &&
    next.serverReachable === state.serverReachable &&
    status === state.status
  ) {
    return;
  }
  state = {
    online: next.online,
    serverReachable: next.serverReachable,
    status,
  };
  listeners.forEach((listener) => listener());
}

/** Report a successful API response — server is reachable. */
export function markApiSuccess(): void {
  setState({ online: state.online, serverReachable: true });
}

/** Report a failed API response (no response, timeout, or 5xx) — server looks down. */
export function markApiFailure(): void {
  // Only meaningful while the device reports itself online. When offline, the
  // `online` flag already drives the derived status.
  setState({ online: state.online, serverReachable: false });
}

/** Update device-online status from the browser's online/offline events. */
export function setOnline(online: boolean): void {
  // On reconnect, optimistically assume reachable; a real call corrects it.
  setState({ online, serverReachable: online ? true : false });
}

/** Subscribe to store changes. Returns an unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable snapshot for useSyncExternalStore. */
export function getSnapshot(): ConnectivityState {
  return state;
}

/** Reset to defaults. Intended for tests. */
export function resetConnectivity(): void {
  setState({ online: true, serverReachable: true });
}
